import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faCog, faSignOutAlt, faSlidersH, faBell, faRobot } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-modal';
import { Menu, Transition } from '@headlessui/react';
import { useLogin } from '../contexts/LoginContext';
import StatusHeader from './StatusHeader';
import WhatsNewDropdown from './WhatsNewDropdown';
import CONFIG from '../config/config';
import './Header.css';
import PropTypes from 'prop-types';
import AboutModal from './AboutModal';
import { useNavigate } from 'react-router-dom';
import journeyTracker from '../utils/journeyTracker';

Modal.setAppElement('#root');

// Add this near the top of the file, outside the component
const MOBILE_WARNING_DISMISSED_KEY = 'mobileWarningDismissed';

const Header = ({
  onLogout,
  loggedInUser,
  trialDaysLeft,
  isSubscribed,
  statusMessage,
  statusType,
  appStatus,
  onProfileClick,
  onNotificationsClick,
  onMcpClick,
  onSubscriptionClick,
  onSettingsSave,
  onPreferencesClick
}) => {
  // Upgrade modal disabled — will be re-implemented with new tier system
  // const [modalIsOpen, setModalIsOpen] = useState(false);
  const { triggerLogin } = useLogin();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showDeleteSubmenu, setShowDeleteSubmenu] = useState(false);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const navigate = useNavigate();

  const toggleDropdown = () => {
    console.log('Toggling dropdown');
    setIsDropdownOpen(prev => {
      console.log('New dropdown state:', !prev);
      return !prev;
    });
  };

  // Auth check removed - consolidated in App.js checkAuth effect to prevent race conditions

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add this useEffect to detect mobile devices
  useEffect(() => {
    const checkMobileDevice = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
      const warningDismissed = localStorage.getItem(MOBILE_WARNING_DISMISSED_KEY);
      setShowMobileWarning(isMobileDevice && !warningDismissed);
    };

    checkMobileDevice();
    window.addEventListener('resize', checkMobileDevice);

    return () => {
      window.removeEventListener('resize', checkMobileDevice);
    };
  }, []);

  const getUserInitials = (user) => {
    if (!user) return '';
    const displayName = typeof user === 'string' ? user : (user.name || user.email);
    return displayName
      .split(' ')
      .map(name => name[0].toUpperCase())
      .join('');
  };

  const handleLogout = () => {
    // Token cleanup happens in App's handleLogout (onLogout)
    onLogout();
    setIsDropdownOpen(false);

    // Add logout event tracking
    if (window.amplitude) {
      window.amplitude.track('User Logout');
    }
  };

  // Upgrade modal/subscription handlers disabled — will be re-implemented with new tier system
  // const openModal = () => {
  //   journeyTracker.trackAction('upgrade_modal_open');
  //   setModalIsOpen(true);
  // };
  // const closeModal = () => setModalIsOpen(false);
  // const handleSubscribe = () => closeModal();
  // const handleSubscriptionClick = () => {
  //   window.open('https://billing.stripe.com/p/login/00g9Er4TpfdqdHOcMM?prefilled_promo_code=VfjF0RPi', '_blank');
  //   journeyTracker.trackAction('subscription_click');
  //   if (window.amplitude) window.amplitude.track('Subscription Click');
  // };

  const handleDeleteProfile = async () => {
    if (window.confirm("Are you sure you want to delete your profile? This action cannot be undone.")) {
      try {
        const response = await fetch(`${CONFIG.API_URL}/api/user-data?userId=${encodeURIComponent(loggedInUser.email)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (response.ok) {
          handleLogout();
          alert("Your profile has been deleted successfully.");
        } else {
          throw new Error('Failed to delete profile');
        }
      } catch (error) {
        console.error('Error deleting profile:', error);
        alert("An error occurred while deleting your profile. Please try again later.");
      }
    }
  };

  const handleDismissMobileWarning = useCallback(() => {
    localStorage.setItem(MOBILE_WARNING_DISMISSED_KEY, 'true');
    setShowMobileWarning(false);
  }, []);

  const handleLogoClick = () => {
    navigate('/app');
  };

  return (
    <>
      <Modal
        isOpen={showMobileWarning}
        onRequestClose={handleDismissMobileWarning}
        contentLabel="Mobile Device Warning"
        className="mobile-warning-modal"
        overlayClassName="mobile-warning-overlay"
      >
        <div className="mobile-warning-content" data-nosnippet>
          <h2>Desktop Experience Recommended</h2>
          <p>This application is optimized exclusively for desktop use at the moment. We are working on a mobile version.</p>
          <button 
            onClick={handleDismissMobileWarning}
            className="mobile-warning-button"
          >
            I Understand
          </button>
        </div>
      </Modal>
      {appStatus && (
        <div 
          className={`status-bar ${appStatus.type}`}
        >
          {appStatus.message}
        </div>
      )}
      <div className="header">
        <div className="logo" onClick={handleLogoClick}>
          <img src="/quantape.png" alt="QuantApe AI Stock Screener Logo" className="logo-image" />
          <div className="title-container" data-nosnippet>
            <h1>QuantApe Smart Screener <span className="experiment-badge">beta</span></h1>
            <h2>Signals behind tomorrow&rsquo;s market moves</h2>
          </div>
        </div>
        <div className="right-section">
          <WhatsNewDropdown />
          <div className="about-button" onClick={() => setAboutModalOpen(true)}>
            <span>About</span>
          </div>
          {/* Upgrade button disabled — will be re-implemented with new tier system
          {!isSubscribed && trialDaysLeft > 0 && !isMobile && (
            <div className="upgrade-button" onClick={openModal}>
              <span>Upgrade</span>
            </div>
          )}
          */}
          {loggedInUser ? (
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button className="user-icon-button">
                <div className="user-icon">
                  <span className="user-initials">{getUserInitials(loggedInUser)}</span>
                </div>
              </Menu.Button>
              <Transition
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  style={{ minWidth: '14rem' }}
                >
                  <div className="px-1 py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={onProfileClick}
                          className={`${
                            active ? 'bg-blue-500 text-white' : 'text-gray-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <FontAwesomeIcon icon={faUser} className="mr-2" />
                          Profile
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={onNotificationsClick}
                          className={`${
                            active ? 'bg-blue-500 text-white' : 'text-gray-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <FontAwesomeIcon icon={faBell} className="mr-2" />
                          Notifications
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={onMcpClick}
                          className={`${
                            active ? 'bg-blue-500 text-white' : 'text-gray-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <FontAwesomeIcon icon={faRobot} className="mr-2" />
                          MCP Server
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={onPreferencesClick}
                          className={`${
                            active ? 'bg-blue-500 text-white' : 'text-gray-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <FontAwesomeIcon icon={faSlidersH} className="mr-2" />
                          Preferences
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-blue-500 text-white' : 'text-gray-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                          Logout
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          ) : (
            <button className="google-login-button" onClick={() => triggerLogin()}>
              <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: '8px' }} aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in
            </button>
          )}
        </div>
        {/* Stripe pricing modal disabled — will be re-implemented with new tier system
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          contentLabel="Subscription Plans"
          className="modal-content"
          overlayClassName="modal-overlay"
        >
          <button onClick={closeModal} className="close-modal-button">X</button>
          <script async src="https://js.stripe.com/v3/pricing-table.js"></script>
          <stripe-pricing-table
            pricing-table-id="prctbl_1SjifUK2h01IjPAiWkDmpP4O"
            publishable-key="pk_live_01aAutTYAQHsauVPglKbqeln00sTJBIfF2"
            default-promotion-code="VfjF0RPi"
          />
        </Modal>
        */}
        <AboutModal
          isOpen={aboutModalOpen}
          onClose={() => setAboutModalOpen(false)}
        />
      </div>
    </>
  );
};

Header.propTypes = {
  onLogout: PropTypes.func.isRequired,
  loggedInUser: PropTypes.shape({
    email: PropTypes.string.isRequired,
    name: PropTypes.string
  }),
  trialDaysLeft: PropTypes.number,
  isSubscribed: PropTypes.bool,
  statusMessage: PropTypes.string,
  statusType: PropTypes.string,
  appStatus: PropTypes.shape({
    message: PropTypes.string,
    type: PropTypes.string
  }),
  onProfileClick: PropTypes.func.isRequired,
  onNotificationsClick: PropTypes.func,
  onMcpClick: PropTypes.func,
  onSubscriptionClick: PropTypes.func,
  onSettingsSave: PropTypes.func.isRequired,
  onPreferencesClick: PropTypes.func
};

Header.defaultProps = {
  loggedInUser: null,
  trialDaysLeft: 0,
  isSubscribed: false,
  statusMessage: '',
  statusType: 'info',
  appStatus: null
};

export default Header;
